// Client-side mirror of supabase/functions/_shared/deep-links.ts.
// Use this when writing notifications.action_url or constructing in-app share links.
// Keep these two files in sync.

export type DeepLinkKind =
  | "chat_message"
  | "chat_call"
  | "order"
  | "booking"
  | "todo"
  | "waitlist_signup"
  | "admin_error_incident"
  | "admin_abuse_report"
  | "admin_feedback"
  | "channel_post"
  | "fork_link"
  | "app_root";

export interface DeepLinkPayload {
  conversation_id?: string | null;
  message_id?: string | null;
  order_id?: string | null;
  booking_id?: string | null;
  todo_id?: string | null;
  user_id?: string | null;
  fingerprint?: string | null;
  report_id?: string | null;
  feedback_id?: string | null;
  promo_id?: string | null;
}

function joinPath(baseUrl: string, path: string): string {
  const base = (baseUrl || "").replace(/\/+$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return `${base}${p}`;
}

export function buildDeepLink(
  kind: DeepLinkKind,
  payload: DeepLinkPayload = {},
  baseUrl = "",
): string {
  const path = (() => {
    switch (kind) {
      case "chat_message": {
        if (!payload.conversation_id) return "/chats";
        const q = payload.message_id ? `?msg=${encodeURIComponent(payload.message_id)}` : "";
        return `/chat/${payload.conversation_id}${q}`;
      }
      case "chat_call":
        return payload.conversation_id ? `/chat/${payload.conversation_id}?call=1` : "/chats";
      case "channel_post": {
        if (!payload.conversation_id) return "/chats";
        const q = payload.message_id ? `?msg=${encodeURIComponent(payload.message_id)}` : "";
        return `/chat/${payload.conversation_id}${q}`;
      }
      case "order":
        return payload.order_id ? `/orders/${payload.order_id}` : "/orders";
      case "booking":
        return payload.conversation_id
          ? `/chat/${payload.conversation_id}?booking=${encodeURIComponent(payload.booking_id || "")}`
          : "/chats";
      case "todo":
        return payload.todo_id ? `/todos?id=${encodeURIComponent(payload.todo_id)}` : "/todos";
      case "waitlist_signup":
        return payload.user_id
          ? `/admin?tab=ai-users&user=${encodeURIComponent(payload.user_id)}`
          : "/admin?tab=ai-users";
      case "admin_error_incident":
        return payload.fingerprint
          ? `/admin?tab=health&subtab=incidents&fp=${encodeURIComponent(payload.fingerprint)}`
          : "/admin?tab=health&subtab=incidents";
      case "admin_abuse_report":
        return payload.report_id
          ? `/admin?tab=comms&subtab=reports&report=${encodeURIComponent(payload.report_id)}`
          : "/admin?tab=comms&subtab=reports";
      case "admin_feedback":
        return payload.feedback_id
          ? `/admin?tab=comms&subtab=feedback&feedback=${encodeURIComponent(payload.feedback_id)}`
          : "/admin?tab=comms&subtab=feedback";
      case "fork_link":
        return payload.conversation_id ? `/chat/${payload.conversation_id}` : "/chats";
      case "app_root":
      default:
        return "/";
    }
  })();
  return baseUrl ? joinPath(baseUrl, path) : path;
}
